import { useEffect, useState } from 'react';
import { API_URL } from '../utils/apiBase';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * usePartners — one-time fetch of the Provider Registry (GET /api/partners).
 * Cached at module scope so every consumer shares the same map.
 *
 * Sprint B / iter82 mandate: The UI must become provider-agnostic. Adding a
 * new partner (Agoda, Expedia, Hotels.com, Trip.com, Kiwi, Omio, Trainline,
 * Rail Europe, GetYourGuide, Viator, Ferryhopper, Direct Ferries…) becomes
 * a single dict entry in /app/backend/partners.py — no UI changes required.
 *
 * Consumers:
 *   const { getPartner } = usePartners();
 *   const meta = getPartner('booking');   // {name, tagline, trust_bullets, verified, ...}
 *
 * `getPartner` degrades gracefully: for unknown keys it returns a sparse
 * fallback (never null, never throws) so RedirectOverlay never breaks.
 */

// Module-scope cache keyed by language ('en' | 'bg')
const CACHE = { en: null, bg: null };
let INFLIGHT = { en: null, bg: null };

async function fetchOnce(language) {
  const lang = language === 'bg' ? 'bg' : 'en';
  if (CACHE[lang]) return CACHE[lang];
  if (!INFLIGHT[lang]) {
    INFLIGHT[lang] = fetch(`${API_URL}/api/partners?language=${lang}`)
      .then((r) => (r.ok ? r.json() : { partners: [] }))
      .then((data) => {
        const map = {};
        (data?.partners || []).forEach((p) => { map[p.key] = p; });
        CACHE[lang] = map;
        return map;
      })
      .catch(() => ({}));
  }
  return INFLIGHT[lang];
}

function fallbackPartner(key, lang) {
  const isBg = lang === 'bg';
  return {
    key: key || 'unknown',
    name: key || (isBg ? 'Външен доставчик' : 'External provider'),
    type: 'trip',
    tagline: isBg ? 'Проверете доставчика и условията преди плащане' : 'Review the supplier and terms before payment',
    verified: false,
    iata_accredited: false,
    trust_bullets: isBg
      ? [
        'Резервацията се извършва извън TravelAIBG',
        'Проверете точната цена и наличност',
        'Прочетете условията за плащане и отказ',
      ]
      : [
        'The reservation takes place outside TravelAIBG',
        'Verify the final price and availability',
        'Read payment and cancellation conditions',
      ],
  };
}

export default function usePartners() {
  const { language } = useLanguage();
  const lang = language === 'bg' ? 'bg' : 'en';
  const [map, setMap] = useState(CACHE[lang] || {});

  useEffect(() => {
    let mounted = true;
    fetchOnce(lang).then((m) => {
      if (mounted) setMap(m);
    });
    return () => { mounted = false; };
  }, [lang]);

  function getPartner(key) {
    const k = String(key || '').toLowerCase();
    return map[k] || fallbackPartner(key, lang);
  }

  return { getPartner, ready: Object.keys(map).length > 0 };
}
